import React, { TextareaHTMLAttributes, forwardRef } from 'react';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    label?: string;
    error?: string;
    helperText?: string;
    fullWidth?: boolean;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
    (
        {
            label,
            error,
            helperText,
            fullWidth = false,
            className = '',
            id,
            rows = 4,
            ...props
        },
        ref
    ) => {
        const textareaId = id || `textarea-${label?.replace(/\s+/g, '-').toLowerCase()}`;

        return (
            <div className={`${fullWidth ? 'w-full' : ''}`}>
                {label && (
                    <label
                        htmlFor={textareaId}
                        className="block mb-2 text-sm font-medium text-[#015FA6] font-manrope"
                    >
                        {label}
                    </label>
                )}
                <textarea
                    ref={ref}
                    id={textareaId}
                    rows={rows}
                    aria-invalid={error ? 'true' : 'false'}
                    aria-describedby={error ? `${textareaId}-error` : helperText ? `${textareaId}-helper` : undefined}
                    className={`
                        w-full px-4 py-3
                        font-manrope text-base
                        text-gray-700 placeholder:text-[#015FA6]/50
                        bg-white border rounded-lg
                        transition-all duration-200
                        resize-y min-h-[100px]
                        focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20
                        disabled:bg-gray-100 disabled:cursor-not-allowed
                        ${error ? 'border-red-500 focus:border-red-500' : 'border-[#CBD5E0] focus:border-[#015FA6]'}
                        ${className}
                    `}
                    {...props}
                />
                {error && (
                    <p
                        id={`${textareaId}-error`}
                        role="alert"
                        className="mt-1.5 text-xs text-red-500 font-manrope flex items-center gap-1"
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
                            <path
                                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                                stroke="currentColor"
                                strokeWidth={1.5}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        {error}
                    </p>
                )}
                {helperText && !error && (
                    <p id={`${textareaId}-helper`} className="mt-1.5 text-xs text-gray-500 font-manrope">
                        {helperText}
                    </p>
                )}
            </div>
        );
    }
);

Textarea.displayName = 'Textarea';

export default Textarea;
